import { pixelEventId, type TikTokTrackingEvent, type TrackingPixels } from "@ternary/contracts"
import { browserUuid } from "./browser-id.js"
import { CONSENT_KEY, readConsent } from "./signup/tracking.js"

// `<signupId>:<EventName>` (ruling 4), from the contracts so the server events share the one id.
export { pixelEventId }

type QueueFunction = ((...arguments_: unknown[]) => void) & {
  callMethod?: (...arguments_: unknown[]) => void
  disablePushState?: boolean
  loaded?: boolean
  push?: QueueFunction
  queue?: unknown[][]
  version?: string
}

type AnalyticsWindow = Window & {
  _fbq?: QueueFunction
  dataLayer?: unknown[]
  fbq?: QueueFunction
  gtag?: (...arguments_: unknown[]) => void
  ttq?: TikTokQueue
}

type TikTokPixelQueue = Array<unknown[]> & {
  track?: (name: NonNullable<TikTokTrackingEvent>["name"]) => void
}

type TikTokQueue = Array<unknown[]> & {
  _i?: Record<string, TikTokPixelQueue>
  instance?: (id: string) => TikTokPixelQueue
  load?: (id: string) => void
  page?: (...arguments_: unknown[]) => void
  revokeConsent?: (...arguments_: unknown[]) => void
  track?: (...arguments_: unknown[]) => void
}

/**
 * A pixel event the signup pages send. Lead and CompleteRegistration carry `pixelEventId`; each
 * PageView gets a fresh id in `pixelCommands`.
 */
export type PixelEvent =
  | { name: "PageView"; path: string }
  | { name: "Lead" | "CompleteRegistration"; eventId: string }

/** One call to a vendor's base code: `fbq(...args)`, `gtag(...args)` or `ttq[args[0]](...rest)`. */
export type PixelCommand = { provider: "meta" | "google" | "tiktok"; args: unknown[] }

function appendProviderScript(id: string, source: string) {
  if (document.getElementById(id)) {
    return
  }
  const script = document.createElement("script")
  script.async = true
  script.id = id
  script.src = source
  document.head.append(script)
}

function installTikTokPixel(analytics: AnalyticsWindow, pixelId: string) {
  analytics.ttq ??= []
  const queue = analytics.ttq
  queue._i ??= {}
  queue.instance ??= (id: string) => {
    queue._i ??= {}
    const pixels = queue._i
    if (!pixels[id]) {
      const pixel: TikTokPixelQueue = []
      pixel.track = (name) => {
        pixel.push(["track", name])
      }
      pixels[id] = pixel
    }
    return pixels[id]
  }
  queue.load ??= (id: string) => {
    queue.instance?.(id)
    appendProviderScript(
      `ternary-tiktok-pixel-${id}`,
      `https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=${encodeURIComponent(id)}&lib=ttq`,
    )
  }
  // TikTok's base code queues [method, ...args] on ttq until events.js takes the queue over.
  queue.page ??= (...arguments_: unknown[]) => {
    queue.push(["page", ...arguments_])
  }
  queue.track ??= (...arguments_: unknown[]) => {
    queue.push(["track", ...arguments_])
  }
  queue.revokeConsent ??= (...arguments_: unknown[]) => {
    queue.push(["revokeConsent", ...arguments_])
  }
  queue.load(pixelId)
}

/**
 * Loads each configured vendor's library as an external script and sets up its standard queue, so
 * calls made before the library arrives reach it. A provider whose script is already on the page is
 * skipped, so a re-render repeats nothing. Only installConsentedPixels calls this.
 */
function installTrackingPixels(pixels: TrackingPixels) {
  const analytics = window as AnalyticsWindow
  watchConsentWithdrawal()
  // Each vendor on its own, so one that throws (a broken global an extension left, say) never
  // stops the others from installing (M8).
  const vendor = (install: () => void) => {
    try {
      install()
    } catch {
      // That vendor stays out; the page and the other vendors carry on.
    }
  }

  vendor(() => installGoogle(analytics, pixels))
  vendor(() => installMeta(analytics, pixels))
  vendor(() => {
    if (pixels.tiktok && !document.getElementById(`ternary-tiktok-pixel-${pixels.tiktok}`)) {
      installTikTokPixel(analytics, pixels.tiktok)
    }
  })
}

function installGoogle(analytics: AnalyticsWindow, pixels: TrackingPixels) {
  if (pixels.google && !document.getElementById("ternary-google-tag")) {
    analytics.dataLayer ??= []
    // Google's own shim: gtag.js processes the Arguments objects it pushes, not arrays.
    analytics.gtag ??= function gtag() {
      analytics.dataLayer ??= []
      // biome-ignore lint/complexity/noArguments: gtag.js reads only Arguments objects.
      analytics.dataLayer.push(arguments)
    }
    appendProviderScript(
      "ternary-google-tag",
      `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(pixels.google)}`,
    )
    analytics.gtag("js", new Date())
    analytics.gtag("config", pixels.google, { send_page_view: false })
  }
}

function installMeta(analytics: AnalyticsWindow, pixels: TrackingPixels) {
  if (pixels.meta && !document.getElementById("ternary-meta-pixel")) {
    if (!analytics.fbq) {
      // Meta's base code queues until fbevents.js sets callMethod, then hands every call to it.
      const queue: QueueFunction = (...arguments_: unknown[]) => {
        if (queue.callMethod) {
          queue.callMethod.apply(queue, arguments_)
        } else {
          queue.queue?.push(arguments_)
        }
      }
      queue.loaded = true
      queue.push = queue
      queue.queue = []
      queue.version = "2.0"
      analytics.fbq = queue
      analytics._fbq = queue
    }
    // No automatic events: history-change page views (which would double the explicit PageView)
    // and the button-click and page-metadata events. The event table is the whole set.
    analytics.fbq.disablePushState = true
    appendProviderScript("ternary-meta-pixel", "https://connect.facebook.net/en_US/fbevents.js")
    analytics.fbq("set", "autoConfig", false, pixels.meta)
    analytics.fbq("init", pixels.meta)
  }
}

/**
 * The only way pixels load, on every customer page (R64): it does nothing unless the visitor
 * accepted ad cookies. It never throws, so a broken vendor global cannot reach React (R67).
 */
export function installConsentedPixels(pixels: TrackingPixels) {
  try {
    if (readConsent()?.ads === true) {
      installTrackingPixels(pixels)
    }
  } catch {
    // A pixel never breaks the page.
  }
}

/**
 * Withdraws consent in each vendor library already on the page, so their own collection stops
 * too: the Decline button calls it, and so does a decline made in another tab.
 */
export function revokePixels(): void {
  const analytics = window as AnalyticsWindow
  const revocations = [
    () => analytics.fbq?.("consent", "revoke"),
    () =>
      analytics.gtag?.("consent", "update", {
        ad_personalization: "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        analytics_storage: "denied",
      }),
    () => analytics.ttq?.revokeConsent?.(),
  ]
  for (const revoke of revocations) {
    try {
      revoke()
    } catch {
      // A broken vendor library never breaks the page.
    }
  }
}

let watchingConsent = false

// A decline (or cleared storage) in another tab reaches this one as a storage event.
function watchConsentWithdrawal() {
  if (watchingConsent) return
  watchingConsent = true
  window.addEventListener("storage", (event) => {
    if ((event.key === CONSENT_KEY || event.key === null) && readConsent()?.ads !== true) {
      revokePixels()
    }
  })
}

/** Each configured provider's call for `event`, in Meta, Google, TikTok order. */
export function pixelCommands(pixels: TrackingPixels, event: PixelEvent): PixelCommand[] {
  let args: Record<PixelCommand["provider"], unknown[]>
  if (event.name === "PageView") {
    // A fresh id for each view, shared by Meta and Google (§3.9). TikTok's page call takes none.
    const eventId = browserUuid()
    args = {
      google: ["event", "page_view", { event_id: eventId, page_path: event.path }],
      meta: ["track", "PageView", {}, { eventID: eventId }],
      tiktok: ["page"],
    }
  } else {
    args = {
      google:
        event.name === "Lead"
          ? ["event", "generate_lead", { event_id: event.eventId }]
          : ["event", "sign_up", { event_id: event.eventId, method: "discord" }],
      meta: ["track", event.name, {}, { eventID: event.eventId }],
      tiktok: ["track", event.name, {}, { event_id: event.eventId }],
    }
  }
  return (["meta", "google", "tiktok"] as const)
    .filter((provider) => pixels[provider])
    .map((provider) => ({ args: args[provider], provider }))
}

/** Calls the vendor's global, looked up now, since its library replaces the queue as it loads. */
function sendPixelCommand({ args, provider }: PixelCommand) {
  const analytics = window as AnalyticsWindow
  if (provider === "meta") {
    analytics.fbq?.(...args)
  } else if (provider === "google") {
    analytics.gtag?.(...args)
  } else {
    const [method, ...rest] = args
    if (method === "page" || method === "track") {
      analytics.ttq?.[method]?.(...rest)
    }
  }
}

/**
 * Sends `event` to each configured pixel, only once the visitor accepted ad cookies. A missing,
 * blocked or failing vendor library is skipped: this never throws.
 */
export function firePixelEvent(pixels: TrackingPixels | null | undefined, event: PixelEvent): void {
  try {
    if (!pixels || readConsent()?.ads !== true) {
      return
    }
    for (const command of pixelCommands(pixels, event)) {
      try {
        sendPixelCommand(command)
      } catch {
        // One vendor failing never stops the others.
      }
    }
  } catch {
    // A pixel never breaks signup.
  }
}

/** The TikTok conversion the legacy purchase and order pages send, after consent only. */
export function trackCustomerTikTokEvent(event: TikTokTrackingEvent) {
  if (!event || readConsent()?.ads !== true) {
    return
  }
  const analytics = window as AnalyticsWindow
  installTikTokPixel(analytics, event.pixelId)
  analytics.ttq?.instance?.(event.pixelId).track?.(event.name)
}

/**
 * The legacy purchase and order pages: once the visitor accepted ad cookies, load the pixels and
 * send the page view and the TikTok conversion. Without consent it does nothing.
 */
export function trackConsentedCustomerPage(
  pixels: TrackingPixels,
  conversionEvent: TikTokTrackingEvent,
) {
  installConsentedPixels(pixels)
  firePixelEvent(pixels, { name: "PageView", path: window.location.pathname })
  trackCustomerTikTokEvent(conversionEvent)
}
